/**
 * Luno 3D Product Viewer
 * Interactive 3D model viewer with color variants, hotspots, and camera controls
 */

(function () {
  'use strict';

  const Luno3DViewer = {
    instances: {},
    lazySections: {}, // Track sections waiting for lazy load
    modelViewerLibraryLoaded: false,
    modelViewerLibraryLoading: false,
    modelViewerLoadCallbacks: [],

    // Camera view presets
    cameraPresets: {
      front: { orbit: '0deg 75deg 5m', target: '0m 0m 0m' },
      back: { orbit: '180deg 75deg 5m', target: '0m 0m 0m' },
      side: { orbit: '90deg 75deg 5m', target: '0m 0m 0m' },
      top: { orbit: '0deg 0deg 5m', target: '0m 0m 0m' },
    },

    // Navigation sequence for previous/next buttons
    viewSequence: ['front', 'side', 'back', 'top'],

    /**
     * Register a section for lazy loading (popup mode)
     */
    registerLazySection: function (sectionId) {
      const sectionElement = document.querySelector(`#luno-3d-viewer-${sectionId}`);
      if (!sectionElement) {
        console.warn('Section not found for lazy load:', sectionId);
        return;
      }

      this.lazySections[sectionId] = {
        sectionElement: sectionElement,
        initialized: false,
      };

      console.log(`📦 Section ${sectionId} registered for lazy loading`);
    },

    /**
     * Load model-viewer library dynamically
     */
    loadModelViewerLibrary: function (callback) {
      // Check if model-viewer is already defined (loaded by section mode)
      if (typeof customElements !== 'undefined' && customElements.get('model-viewer')) {
        this.modelViewerLibraryLoaded = true;
        callback();
        return;
      }

      // If already loaded, call callback immediately
      if (this.modelViewerLibraryLoaded) {
        callback();
        return;
      }

      // Add callback to queue
      this.modelViewerLoadCallbacks.push(callback);

      // If already loading, wait for existing load
      if (this.modelViewerLibraryLoading) {
        return;
      }

      // Start loading
      this.modelViewerLibraryLoading = true;
      console.log('📥 Loading model-viewer library...');

      const script = document.createElement('script');
      script.type = 'module';
      script.src = 'https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js';

      script.onload = () => {
        this.modelViewerLibraryLoaded = true;
        this.modelViewerLibraryLoading = false;
        console.log('✅ Model-viewer library loaded');

        // Call all queued callbacks
        this.modelViewerLoadCallbacks.forEach((cb) => cb());
        this.modelViewerLoadCallbacks = [];
      };

      script.onerror = () => {
        this.modelViewerLibraryLoading = false;
        console.error('❌ Failed to load model-viewer library');
      };

      document.head.appendChild(script);
    },

    /**
     * Initialize a lazy-loaded section
     */
    initLazySection: function (sectionId, callback) {
      if (!this.lazySections[sectionId]) {
        console.warn('Section not registered for lazy loading:', sectionId);
        return;
      }

      if (this.lazySections[sectionId].initialized) {
        console.log('Section already initialized:', sectionId);
        if (callback) callback();
        return;
      }

      console.log('🚀 Initializing lazy section:', sectionId);

      // Load library if needed
      this.loadModelViewerLibrary(() => {
        // Wait a bit for custom element to be defined
        setTimeout(() => {
          this.init(sectionId);
          this.lazySections[sectionId].initialized = true;
          if (callback) callback();
        }, 100);
      });
    },

    /**
     * Initialize a 3D viewer instance
     */
    init: function (sectionId) {
      const modelViewer = document.querySelector(`#model-viewer-${sectionId}`);
      if (!modelViewer) {
        console.warn('Model viewer not found for section:', sectionId);
        return;
      }

      // Store instance data
      this.instances[sectionId] = {
        viewer: modelViewer,
        currentViewIndex: 0,
        defaultOrbit: modelViewer.getAttribute('camera-orbit'),
        isLoading: true,
        activeHotspotSlot: null, // Track active hotspot for Z-index enforcement
      };

      // Initialize all features
      this.initLoadingState(sectionId);
      this.initSlideUpAnimation(sectionId);
      // this.init3DDebugger(sectionId); // For hotspot debugging
      this.initColorVariants(sectionId);
      this.initHotspots(sectionId);
      this.initCameraControls(sectionId);
      this.initNavigation(sectionId);
      this.initAccessibility(sectionId);
      this.initAnimation(sectionId);
      this.initCtaScroll(sectionId);

      // Inject custom styles into Shadow DOM
      this.injectShadowStyles(sectionId);
    },

    /**
     * Inject custom styles into Shadow DOM to override z-index
     */
    injectShadowStyles: function (sectionId) {
      const instance = this.instances[sectionId];
      const modelViewer = instance.viewer;

      customElements.whenDefined('model-viewer').then(() => {
        setTimeout(() => {
          if (!modelViewer.shadowRoot) return;

          // Check if we already injected styles
          if (modelViewer.shadowRoot.querySelector('#luno-shadow-styles')) return;

          const style = document.createElement('style');
          style.id = 'luno-shadow-styles';
          style.textContent = `
            .luno-active-wrapper {
              z-index: 1000 !important;
            }
          `;
          modelViewer.shadowRoot.appendChild(style);
          console.log('💉 Injected Shadow DOM styles');
        }, 100);
      });
    },

    /**
     * Loading state management
     */
    initLoadingState: function (sectionId) {
      const instance = this.instances[sectionId];
      const modelViewer = instance.viewer;
      const loadingOverlay = document.querySelector(`#loading-${sectionId}`);
      const progressBar = document.querySelector(`#progress-${sectionId}`);

      // Show loading overlay
      if (loadingOverlay) {
        loadingOverlay.style.display = 'flex';
      }

      // Track loading progress
      modelViewer.addEventListener('progress', (event) => {
        const progress = event.detail.totalProgress;
        if (progressBar) {
          progressBar.style.width = `${progress * 100}%`;
        }
      });

      // Handle load complete
      modelViewer.addEventListener('load', () => {
        instance.isLoading = false;
        if (loadingOverlay) {
          setTimeout(() => {
            loadingOverlay.style.opacity = '0';
            setTimeout(() => {
              loadingOverlay.style.display = 'none';
            }, 300);
          }, 500);
        }
      });

      // Handle errors
      modelViewer.addEventListener('error', (event) => {
        console.error('Model loading error:', event);
        if (loadingOverlay) {
          const loadingText = loadingOverlay.querySelector('.luno-3d-loading-text');
          if (loadingText) {
            loadingText.textContent = 'Error loading 3D model. Please refresh the page.';
          }
        }
      });
    },

    /**
     * Color variant switching
     */
    initColorVariants: function (sectionId) {
      const instance = this.instances[sectionId];
      const modelViewer = instance.viewer;
      const colorSwatches = document.querySelectorAll(`#luno-3d-viewer-${sectionId} .luno-color-swatch`);
      const colorNameDisplay = document.querySelector(`#color-name-${sectionId}`);

      if (colorSwatches.length === 0) return;

      colorSwatches.forEach((swatch) => {
        swatch.addEventListener('click', async () => {
          // Update active state
          colorSwatches.forEach((s) => s.classList.remove('active'));
          swatch.classList.add('active');

          // Update color name display
          const colorName = swatch.getAttribute('data-color');
          if (colorNameDisplay) {
            colorNameDisplay.textContent = colorName.charAt(0).toUpperCase() + colorName.slice(1);
          }

          // Get color values and switch material
          const colorValue = swatch.getAttribute('data-color-value');
          if (colorValue && modelViewer.model) {
            await this.switchMaterialColor(modelViewer, colorValue);
          }
        });
      });
    },

    /**
     * Switch material color using PBR
     */
    switchMaterialColor: async function (modelViewer, colorValue) {
      try {
        await modelViewer.model.materials[0].pbrMetallicRoughness.setBaseColorFactor(this.parseColorValue(colorValue));
      } catch (error) {
        console.error('Error switching material color:', error);
      }
    },

    /**
     * Parse RGB color string to normalized array
     */
    parseColorValue: function (colorString) {
      const rgb = colorString.split(',').map((v) => parseInt(v.trim()));
      return [rgb[0] / 255, rgb[1] / 255, rgb[2] / 255, 1.0];
    },

    /**
     * Initialize hotspots
     */
    initHotspots: function (sectionId) {
      const instance = this.instances[sectionId];
      const modelViewer = instance.viewer;
      const hotspots = document.querySelectorAll(`#luno-3d-viewer-${sectionId} .luno-hotspot`);

      hotspots.forEach((hotspot) => {
        const annotation = hotspot.querySelector('.luno-hotspot-annotation');

        // Store original camera position for reset
        if (!instance.originalCameraOrbit) {
          instance.originalCameraOrbit = modelViewer.getCameraOrbit();
          instance.originalCameraTarget = modelViewer.getCameraTarget();
        }

        if (annotation) {
          // Close button handler
          const closeBtn = hotspot.querySelector('.luno-hotspot-close');
          if (closeBtn) {
            closeBtn.addEventListener('click', (e) => {
              e.stopPropagation(); // Prevent bubbling to hotspot click

              // Close logic
              annotation.classList.remove('visible');
              hotspot.classList.remove('active');

              // Remove active class from Shadow DOM
              const instance = this.instances[sectionId];
              const modelViewer = instance.viewer;
              if (modelViewer.shadowRoot) {
                const allWrappers = modelViewer.shadowRoot.querySelectorAll('.annotation-wrapper');
                allWrappers.forEach((w) => w.classList.remove('luno-active-wrapper'));
              }
            });
          }

          // Toggle annotation on click
          hotspot.addEventListener('click', (e) => {
            e.stopPropagation();
            const isVisible = annotation.classList.contains('visible');

            // Hide all other annotations & Reset Z-Index
            document.querySelectorAll(`#luno-3d-viewer-${sectionId} .luno-hotspot`).forEach((h) => {
              // Skip the currently clicked hotspot for now
              if (h === hotspot) return;

              // Remove visible class
              const otherAnnotation = h.querySelector('.luno-hotspot-annotation');
              if (otherAnnotation) otherAnnotation.classList.remove('visible');
              h.classList.remove('active');

              // RESET SHADOW Z-INDEX: Back to 1
              // We don't need to do this here anymore as the "click" on new hotspot resets all
              // But we keep it for the "close" logic if needed
            });

            // Toggle current annotation
            if (!isVisible) {
              annotation.classList.add('visible');
              hotspot.classList.add('active');

              // Add active class to Shadow DOM wrapper
              if (modelViewer.shadowRoot) {
                // 1. Remove class from all wrappers
                const allWrappers = modelViewer.shadowRoot.querySelectorAll('.annotation-wrapper');
                allWrappers.forEach((w) => w.classList.remove('luno-active-wrapper'));

                // 2. Add class to active wrapper
                const currentSlotName = hotspot.getAttribute('slot');
                const currentSlot = modelViewer.shadowRoot.querySelector(`slot[name="${currentSlotName}"]`);
                if (currentSlot) {
                  const currentWrapper = currentSlot.closest('.annotation-wrapper');
                  if (currentWrapper) {
                    currentWrapper.classList.add('luno-active-wrapper');
                  }
                }
              }

              // Animate camera if enabled
              if (hotspot.getAttribute('data-camera-animation') === 'true') {
                this.animateHotspotCamera(sectionId, hotspot);
              }
            }
          });

          // Close annotation when clicking outside
          document.addEventListener('click', () => {
            annotation.classList.remove('visible');
            hotspot.classList.remove('active');

            // Remove active class from Shadow DOM
            if (modelViewer.shadowRoot) {
              const allWrappers = modelViewer.shadowRoot.querySelectorAll('.annotation-wrapper');
              allWrappers.forEach((w) => w.classList.remove('luno-active-wrapper'));
            }
          });
        }
      });
    },

    /**
     * Animate camera to hotspot position
     */
    animateHotspotCamera: function (sectionId, hotspot) {
      const instance = this.instances[sectionId];
      const modelViewer = instance.viewer;

      // Get camera settings from hotspot data attributes
      const customOrbit = hotspot.getAttribute('data-camera-orbit');
      const customTarget = hotspot.getAttribute('data-camera-target');
      const hotspotPosition = hotspot.getAttribute('data-position');

      let targetOrbit, targetPosition;

      if (customOrbit) {
        // Use custom orbit if provided
        targetOrbit = customOrbit;
      } else if (hotspotPosition) {
        // Auto-calculate orbit to focus on hotspot
        const [x, y, z] = hotspotPosition.split(' ').map((v) => parseFloat(v));
        targetOrbit = this.calculateOrbitToPosition(x, y, z);
      }

      if (customTarget) {
        // Use custom target if provided
        targetPosition = customTarget;
      } else if (hotspotPosition) {
        // Target the hotspot position
        targetPosition = hotspotPosition.replace(/ /g, 'm ') + 'm';
      }

      // Animate camera
      if (targetOrbit) {
        modelViewer.cameraOrbit = targetOrbit;
        console.log('🎥 Camera animated to:', targetOrbit);
      }

      if (targetPosition) {
        modelViewer.cameraTarget = targetPosition;
        console.log('🎯 Camera target:', targetPosition);
      }

      // Store that we've animated (for potential reset functionality)
      instance.cameraAnimated = true;
    },

    /**
     * Calculate optimal camera orbit to view a specific position
     */
    calculateOrbitToPosition: function (x, y, z) {
      // Calculate angle to position
      const theta = Math.atan2(x, z);
      const distance = Math.sqrt(x * x + z * z);
      const phi = Math.atan2(y, distance);

      // Convert to degrees
      const thetaDeg = (theta * 180) / Math.PI;
      const phiDeg = (phi * 180) / Math.PI + 75; // Offset for better viewing angle

      // Calculate optimal distance (closer for detailed view)
      const optimalDistance = 1.2; // meters - adjust as needed

      return `${thetaDeg}deg ${phiDeg}deg ${optimalDistance}m`;
    },

    /**
     * Reset camera to original position
     */
    resetHotspotCamera: function (sectionId) {
      const instance = this.instances[sectionId];
      const modelViewer = instance.viewer;

      if (instance.originalCameraOrbit) {
        const orbit = instance.originalCameraOrbit;
        modelViewer.cameraOrbit = `${orbit.theta}rad ${orbit.phi}rad ${orbit.radius}m`;
      }

      if (instance.originalCameraTarget) {
        const target = instance.originalCameraTarget;
        modelViewer.cameraTarget = `${target.x}m ${target.y}m ${target.z}m`;
      }

      instance.cameraAnimated = false;
      console.log('🔄 Camera reset to original position');
    },

    /**
     * Camera view controls
     */
    initCameraControls: function (sectionId) {
      const instance = this.instances[sectionId];
      const modelViewer = instance.viewer;
      const viewButtons = document.querySelectorAll(`#luno-3d-viewer-${sectionId} .luno-view-btn`);

      viewButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const viewName = button.getAttribute('data-view');

          if (viewName === 'reset') {
            this.resetCamera(sectionId);
            // Also reset hotspot camera if it was animated
            if (instance.cameraAnimated) {
              this.resetHotspotCamera(sectionId);
            }
          } else {
            this.animateToCameraPreset(sectionId, viewName);
          }

          // Update active state
          viewButtons.forEach((btn) => btn.classList.remove('active'));
          button.classList.add('active');
        });
      });
    },

    /**
     * Animate to camera preset
     */
    animateToCameraPreset: function (sectionId, presetName) {
      const instance = this.instances[sectionId];
      const modelViewer = instance.viewer;
      const preset = this.cameraPresets[presetName];

      if (preset) {
        modelViewer.cameraOrbit = preset.orbit;
        modelViewer.cameraTarget = preset.target;

        // Update current view index for navigation
        const index = this.viewSequence.indexOf(presetName);
        if (index !== -1) {
          instance.currentViewIndex = index;
        }
      }
    },

    /**
     * Reset camera to default position
     */
    resetCamera: function (sectionId) {
      const instance = this.instances[sectionId];
      const modelViewer = instance.viewer;

      modelViewer.cameraOrbit = instance.defaultOrbit;
      modelViewer.cameraTarget = 'auto auto auto';
      instance.currentViewIndex = 0;
    },

    /**
     * Previous/Next navigation
     */
    initNavigation: function (sectionId) {
      const instance = this.instances[sectionId];
      const prevButton = document.querySelector(`#luno-3d-viewer-${sectionId} .luno-nav-prev`);
      const nextButton = document.querySelector(`#luno-3d-viewer-${sectionId} .luno-nav-next`);
      const hotspots = document.querySelectorAll(`#luno-3d-viewer-${sectionId} .luno-hotspot`);

      // Check if hotspots exist
      const hasHotspots = hotspots.length > 0;

      if (hasHotspots) {
        // Convert NodeList to Array and store in instance
        instance.hotspotsArray = Array.from(hotspots);
        instance.currentHotspotIndex = 0;

        // Navigation cycles through hotspots
        if (prevButton) {
          prevButton.addEventListener('click', (e) => {
            e.stopPropagation(); // Prevent document click listener from closing annotation
            instance.currentHotspotIndex =
              (instance.currentHotspotIndex - 1 + instance.hotspotsArray.length) % instance.hotspotsArray.length;
            this.navigateToHotspot(sectionId, instance.currentHotspotIndex);
          });
        }

        if (nextButton) {
          nextButton.addEventListener('click', (e) => {
            e.stopPropagation(); // Prevent document click listener from closing annotation
            instance.currentHotspotIndex = (instance.currentHotspotIndex + 1) % instance.hotspotsArray.length;
            this.navigateToHotspot(sectionId, instance.currentHotspotIndex);
          });
        }
      } else {
        // No hotspots - use default camera preset navigation
        if (prevButton) {
          prevButton.addEventListener('click', () => {
            instance.currentViewIndex =
              (instance.currentViewIndex - 1 + this.viewSequence.length) % this.viewSequence.length;
            const viewName = this.viewSequence[instance.currentViewIndex];
            this.animateToCameraPreset(sectionId, viewName);
          });
        }

        if (nextButton) {
          nextButton.addEventListener('click', () => {
            instance.currentViewIndex = (instance.currentViewIndex + 1) % this.viewSequence.length;
            const viewName = this.viewSequence[instance.currentViewIndex];
            this.animateToCameraPreset(sectionId, viewName);
          });
        }
      }
    },

    /**
     * Navigate to specific hotspot by index
     */
    navigateToHotspot: function (sectionId, hotspotIndex) {
      const instance = this.instances[sectionId];
      const hotspot = instance.hotspotsArray[hotspotIndex];

      if (!hotspot) {
        console.warn('Hotspot not found at index:', hotspotIndex);
        return;
      }

      // Hide all annotations first
      document.querySelectorAll(`#luno-3d-viewer-${sectionId} .luno-hotspot-annotation`).forEach((a) => {
        a.classList.remove('visible');
      });

      // Remove active class from all hotspots
      document.querySelectorAll(`#luno-3d-viewer-${sectionId} .luno-hotspot`).forEach((h) => {
        h.classList.remove('active');
      });

      // Get the annotation element
      const annotation = hotspot.querySelector('.luno-hotspot-annotation');

      if (annotation) {
        // Add visible class to show annotation
        annotation.classList.add('visible');
        hotspot.classList.add('active');
        console.log('✅ Annotation shown for hotspot', hotspotIndex + 1);

        // Add active class to Shadow DOM wrapper
        const instance = this.instances[sectionId];
        const modelViewer = instance.viewer;
        if (modelViewer.shadowRoot) {
          // 1. Remove class from all wrappers
          const allWrappers = modelViewer.shadowRoot.querySelectorAll('.annotation-wrapper');
          allWrappers.forEach((w) => w.classList.remove('luno-active-wrapper'));

          // 2. Add class to active wrapper
          const currentSlotName = hotspot.getAttribute('slot');
          const currentSlot = modelViewer.shadowRoot.querySelector(`slot[name="${currentSlotName}"]`);
          if (currentSlot) {
            const currentWrapper = currentSlot.closest('.annotation-wrapper');
            if (currentWrapper) {
              currentWrapper.classList.add('luno-active-wrapper');
            }
          }
        }
      } else {
        console.warn('⚠️ No annotation found for hotspot', hotspotIndex + 1);
      }

      // Trigger camera animation if enabled
      if (hotspot.getAttribute('data-camera-animation') === 'true') {
        this.animateHotspotCamera(sectionId, hotspot);
      }

      console.log(`📍 Navigated to hotspot ${hotspotIndex + 1} of ${instance.hotspotsArray.length}`);
    },

    /**
     * Initialize CTA scroll behavior
     */
    initCtaScroll: function (sectionId) {
      const btn = document.querySelector(
        `#luno-3d-viewer-${sectionId} .luno-3d-cta-button[data-close-viewer-scroll="true"]`
      );
      if (btn) {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.closeAllPopups();

          const productInfo = document.querySelector('product-info');
          if (productInfo) {
            setTimeout(() => {
              const offset = 80;
              const elementPosition = productInfo.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.pageYOffset - offset;

              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
              });
            }, 300);
          }
        });
      }
    },

    /**
     * Accessibility enhancements
     */
    initAccessibility: function (sectionId) {
      const modelViewer = this.instances[sectionId].viewer;

      // Keyboard navigation for camera controls
      modelViewer.addEventListener('keydown', (event) => {
        switch (event.key) {
          case 'ArrowLeft':
            event.preventDefault();
            this.animateToCameraPreset(sectionId, 'side');
            break;
          case 'ArrowRight':
            event.preventDefault();
            this.animateToCameraPreset(sectionId, 'front');
            break;
          case 'ArrowUp':
            event.preventDefault();
            this.animateToCameraPreset(sectionId, 'top');
            break;
          case 'ArrowDown':
            event.preventDefault();
            this.animateToCameraPreset(sectionId, 'back');
            break;
          case 'r':
          case 'R':
            event.preventDefault();
            this.resetCamera(sectionId);
            break;
        }
      });

      // Focus management
      modelViewer.setAttribute('tabindex', '0');
    },

    /**
     * Utility: Get model viewer instance
     */
    getInstance: function (sectionId) {
      return this.instances[sectionId];
    },

    /**
     * Initialize slide-up animation
     */
    initSlideUpAnimation: function (sectionId) {
      const section = document.querySelector(`#luno-3d-viewer-${sectionId}`);
      const modelViewer = this.instances[sectionId].viewer;

      // Check if animation is enabled
      if (!section || !section.getAttribute('data-animation-enabled')) {
        return;
      }

      // Add loaded class when model loads to trigger animation
      modelViewer.addEventListener('load', () => {
        setTimeout(() => {
          modelViewer.classList.add('loaded');
        }, 100);
      });
    },

    /**
     * Initialize 3D coordinate debugger
     */
    init3DDebugger: function (sectionId) {
      const modelViewer = this.instances[sectionId].viewer;

      // Wait for model to load
      modelViewer.addEventListener('load', () => {
        console.log('🎯 3D Coordinate Debugger Active');
        console.log('💡 Click anywhere on the 3D model to get coordinates');
        console.log('📍 Use these coordinates for hotspot positioning');
        console.log('🎥 Type getCurrentCamera() to get current camera position');

        // Expose helper function to window
        window.getCurrentCamera = () => {
          const orbit = modelViewer.getCameraOrbit();
          const target = modelViewer.getCameraTarget();

          console.group('📸 Current Camera Position');
          console.log('🎥 Camera Orbit:');
          console.log(
            `   ${((orbit.theta * 180) / Math.PI).toFixed(1)}deg ${((orbit.phi * 180) / Math.PI).toFixed(
              1
            )}deg ${orbit.radius.toFixed(2)}m`
          );
          console.log('');
          console.log('🎯 Camera Target:');
          console.log(`   ${target.x.toFixed(3)}m ${target.y.toFixed(3)}m ${target.z.toFixed(3)}m`);
          console.log('');
          console.log('📋 Copy & Paste for Hotspot Camera Settings:');
          console.log(
            `Camera Orbit: ${((orbit.theta * 180) / Math.PI).toFixed(1)}deg ${((orbit.phi * 180) / Math.PI).toFixed(
              1
            )}deg ${orbit.radius.toFixed(2)}m`
          );
          console.log(`Camera Target: ${target.x.toFixed(3)}m ${target.y.toFixed(3)}m ${target.z.toFixed(3)}m`);
          console.groupEnd();

          return {
            orbit: `${((orbit.theta * 180) / Math.PI).toFixed(1)}deg ${((orbit.phi * 180) / Math.PI).toFixed(
              1
            )}deg ${orbit.radius.toFixed(2)}m`,
            target: `${target.x.toFixed(3)}m ${target.y.toFixed(3)}m ${target.z.toFixed(3)}m`,
          };
        };

        // Add click handler to get 3D coordinates
        modelViewer.addEventListener('click', (event) => {
          const rect = modelViewer.getBoundingClientRect();
          const x = event.clientX - rect.left;
          const y = event.clientY - rect.top;

          // Get 3D position from 2D click
          const hit = modelViewer.positionAndNormalFromPoint(x, y);

          if (hit) {
            const position = hit.position;
            const normal = hit.normal;

            console.group('🎯 3D Click Position');
            console.log('📍 Position:');
            console.log(`   X: ${position.x.toFixed(3)}`);
            console.log(`   Y: ${position.y.toFixed(3)}`);
            console.log(`   Z: ${position.z.toFixed(3)}`);
            console.log('');
            console.log('📐 Surface Normal:');
            console.log(`   X: ${normal.x.toFixed(3)}`);
            console.log(`   Y: ${normal.y.toFixed(3)}`);
            console.log(`   Z: ${normal.z.toFixed(3)}`);
            console.log('');
            console.log('📋 Copy & Paste for Hotspot:');
            console.log(`Position X: ${position.x.toFixed(3)}`);
            console.log(`Position Y: ${position.y.toFixed(3)}`);
            console.log(`Position Z: ${position.z.toFixed(3)}`);
            console.log(`Normal X: ${normal.x.toFixed(3)}`);
            console.log(`Normal Y: ${normal.y.toFixed(3)}`);
            console.log(`Normal Z: ${normal.z.toFixed(3)}`);
            console.log('');
            console.log('💡 Tip: Rotate camera to desired view, then type getCurrentCamera()');
            console.groupEnd();

            // Visual feedback on click
            this.show3DClickMarker(modelViewer, x, y);
          } else {
            console.log('❌ No surface hit - try clicking directly on the model');
          }
        });
      });
    },

    /**
     * Show visual marker where user clicked (for debugging)
     */
    show3DClickMarker: function (modelViewer, x, y) {
      // Remove existing marker
      const existingMarker = modelViewer.querySelector('.debug-click-marker');
      if (existingMarker) {
        existingMarker.remove();
      }

      // Create temporary marker
      const marker = document.createElement('div');
      marker.className = 'debug-click-marker';
      marker.style.cssText = `
        position: absolute;
        left: ${x}px;
        top: ${y}px;
        width: 12px;
        height: 12px;
        background: #ff0000;
        border: 2px solid #ffffff;
        border-radius: 50%;
        transform: translate(-50%, -50%);
        pointer-events: none;
        z-index: 1000;
        animation: debugMarkerPulse 1s ease-out;
      `;

      modelViewer.appendChild(marker);

      // Remove after animation
      setTimeout(() => marker.remove(), 1000);
    },

    /**
     * Initialize popup mode triggers
     */
    initPopupTriggers: function () {
      // Find all elements with data-luno-3d-trigger attribute
      const triggers = document.querySelectorAll('[data-luno-3d-trigger]');

      triggers.forEach((trigger) => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          const popupId = trigger.getAttribute('data-luno-3d-trigger');
          this.openPopup(popupId);
        });
      });

      // Handle ESC key to close popup
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeAllPopups();
        }
      });
    },

    /**
     * Open popup by ID
     */
    openPopup: function (popupId) {
      const popup = document.querySelector(`[data-popup-id="${popupId}"]`);
      if (!popup) {
        console.warn('Popup not found:', popupId);
        return;
      }

      // Find section ID from popup
      const sectionElement = popup.querySelector('[data-lazy-load="true"]');
      const sectionId = sectionElement ? sectionElement.id.replace('luno-3d-viewer-', '') : null;

      // If lazy section, initialize it first
      if (sectionId && this.lazySections[sectionId] && !this.lazySections[sectionId].initialized) {
        console.log('⏳ Lazy loading 3D viewer...');

        // Show popup with loading state
        popup.style.display = 'flex';
        setTimeout(() => {
          popup.classList.add('active');
        }, 10);

        // Prevent body scroll
        document.body.style.overflow = 'hidden';

        // Show loading overlay
        const loadingOverlay = popup.querySelector(`#loading-${sectionId}`);
        if (loadingOverlay) {
          loadingOverlay.style.display = 'flex';
          const loadingText = loadingOverlay.querySelector('.luno-3d-loading-text');
          if (loadingText) {
            loadingText.textContent = 'Initializing 3D Viewer...';
          }
        }

        // Initialize lazy section
        this.initLazySection(sectionId, () => {
          console.log('✅ 3D viewer ready');
          // Loading overlay will be hidden by the normal loading state handler
        });

        // Setup close handlers
        this.setupPopupCloseHandlers(popup);
      } else {
        // Normal popup opening
        popup.style.display = 'flex';
        setTimeout(() => {
          popup.classList.add('active');
        }, 10);

        // Prevent body scroll
        document.body.style.overflow = 'hidden';

        // Setup close handlers
        this.setupPopupCloseHandlers(popup);
      }
    },

    /**
     * Setup popup close event handlers
     */
    setupPopupCloseHandlers: function (popup) {
      // Close button
      const closeBtn = popup.querySelector('.luno-3d-popup-close');
      if (closeBtn) {
        closeBtn.onclick = () => this.closePopup(popup);
      }

      // Overlay click (but not container)
      popup.onclick = (e) => {
        if (e.target === popup) {
          this.closePopup(popup);
        }
      };
    },

    /**
     * Close specific popup
     */
    closePopup: function (popup) {
      popup.classList.remove('active');
      setTimeout(() => {
        popup.style.display = 'none';
        document.body.style.overflow = '';
      }, 300);
    },

    /**
     * Close all open popups
     */
    closeAllPopups: function () {
      const popups = document.querySelectorAll('.luno-3d-popup-overlay.active');
      popups.forEach((popup) => this.closePopup(popup));
    },

    /**
     * Initialize animation
     */
    initAnimation: function (sectionId) {
      const modelViewer = this.instances[sectionId].viewer;
      modelViewer.addEventListener('load', () => {
        console.log('✅ Model loaded - starting float animation', modelViewer.model.position);

        let time = 0;

        function gentleFloat() {
          time += 0.015; // Speed (lower = slower)
          const yOffset = Math.sin(time) * 0.03; // Height (lower = less movement)

          // if (modelViewer.model) {
          //   modelViewer.model.position.y = yOffset;
          // }

          requestAnimationFrame(gentleFloat);
        }

        setTimeout(() => {
          gentleFloat();
          console.log('🎬 Float animation started');
        }, 500);
      });
    },
  };

  // Expose to global scope
  window.Luno3DViewer = Luno3DViewer;

  // Auto-initialize popup triggers and sections
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      Luno3DViewer.initPopupTriggers();
    });
  } else {
    Luno3DViewer.initPopupTriggers();
  }
})();
